import { GlobalMessages } from '@shared/localization/messages';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import type { EstimationModelResponse } from '@/api/schema/data-contracts';
import { Button } from '@/components/atoms/Button';
import type { CustomDropdownOption } from '@/components/atoms/CustomDropdown';
import { DropdownFormField } from '@/components/atoms/DropdownFormField';
import { TextFormField } from '@/components/atoms/TextFormField';
import { WysiwygFormTextField } from '@/components/atoms/WysiwygFormTextField';
import { ModalStateless } from '@/components/ModalStateless';
import { entityFieldSettings } from '@/constants/entityFieldSettings';
import { useMessages } from '@/hooks/messages';
import { sortExpressions } from '@/utils/sorting';

import { customDirectCostCreationModalMessageDefinitions } from './messages';

interface CustomDirectCostCreationFormModel {
  name: string;
  groupId: string | null;
  description: string;
}

interface CustomDirectCostCreationModalProps {
  estimationModel: EstimationModelResponse;
  defaultGroupId?: string;
  open: boolean;
  lockGroup?: boolean;
  onSave: (formModel: CustomDirectCostCreationFormModel) => void;
  onClose: () => void;
}

export const CustomDirectCostCreationModal: FC<CustomDirectCostCreationModalProps> = ({
  estimationModel,
  defaultGroupId,
  open,
  lockGroup,
  onClose,
  onSave,
}) => {
  const globalMessages = useMessages(GlobalMessages);
  const messages = useMessages(customDirectCostCreationModalMessageDefinitions);

  const estimationGroupsOptions = useMemo<CustomDropdownOption[]>(
    () =>
      estimationModel?.itemGroups
        .filter((x) => !x.forbidCustomEstimationItem)
        .sort(sortExpressions.byOrder)
        .map((itemGroup) => ({
          value: itemGroup.id,
          label: itemGroup.name,
        })) || [],
    [estimationModel?.itemGroups],
  );

  const formModelInitial = useMemo<CustomDirectCostCreationFormModel>(
    () => ({
      name: '',
      description: '',
      groupId: defaultGroupId ?? null,
    }),
    [defaultGroupId],
  );

  const { control, handleSubmit, reset } = useForm<CustomDirectCostCreationFormModel>({
    defaultValues: formModelInitial,
  });

  useEffect(() => {
    reset(formModelInitial);
  }, [open, reset, formModelInitial]);

  return (
    <ModalStateless
      title={messages.title()}
      open={open}
      onClose={onClose}
      size='s'
      buttons={
        <>
          <Button variant='secondary' outlined onClick={onClose}>
            {globalMessages.cancel()}
          </Button>
          <Button variant='primary' onClick={handleSubmit(onSave)}>
            {globalMessages.create()}
          </Button>
        </>
      }
    >
      <form className='space-y-5'>
        <div>
          <label htmlFor='name'>{messages.costName()}</label>
          <TextFormField
            control={control}
            fieldPath='name'
            rules={{
              required: true,
              maxLength: entityFieldSettings.estimationItemEntity.nameMaxLength,
            }}
            autoFocus
          />
        </div>
        <div>
          <label htmlFor='groupId'>{messages.estimationGroup()}</label>
          {/* EWM-1002: required, because the modal can now be opened with no group preselected
              (from the empty cost table). Without the rule, Create submitted groupId: null and the
              caller's "Group must be chosen" throw was the only thing standing between the user and
              a silent failure. */}
          <DropdownFormField
            disabled={lockGroup}
            control={control}
            fieldPath='groupId'
            options={estimationGroupsOptions}
            rules={{ required: true }}
            fullWidth
          />
        </div>
        <div>
          <label htmlFor='description'>{messages.description()}</label>
          <WysiwygFormTextField
            control={control}
            fieldPath='description'
            placeholder={messages.descriptionPlaceholder()}
            rules={{
              maxLength: entityFieldSettings.estimationItemEntity.descriptionMaxLength,
            }}
          />
        </div>
      </form>
    </ModalStateless>
  );
};
